import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { faPlusCircle } from "@fortawesome/free-solid-svg-icons/faPlusCircle";
import { resources } from "@constants/hardcoded-strings";
import { useModalContext } from "@context/use-modal-context";
import { ORGANIZATION_INVITATIONS_REFERENCE_DATA_TYPES, OrganizationInvitationsReferenceData } from "@constants/reference-data";
import { ReferenceDataSourceTypes } from "@enums/reference-data-source-types";
import { CommonReferenceDataPageTypes } from "@enums/common-reference-data-page-types";
import { ModalTypes } from "@enums/modal-types";
import { useReferenceDataSelector } from "@hooks/use-reference-data-selector";
import { getOrganizationInvitationsReduxRequest } from "@redux-requests/organization-invitations";
import { getCommonReferenceDataReduxRequest } from "@redux-requests/reference-data/get-common-reference-data-redux-request";
import { getAllowedAxnAdminUserPropertyBundles, getAllowedRelyingPartiesForOrganizationId } from "@utils/validation/organization/organization-users/organization-users-util";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { ButtonWithIcon } from "@containers/common/button-with-icon/button-with-icon";
import { LoadingIndicator } from "@containers/common/loading-indicator/loading-indicator";
import { CardContainer } from "@containers/common/card-container/card-container";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { AddOrganizationUserModal } from "../common/add-organization-user-modal/add-organization-user-modal";
import { OrganizationInvitationsTableContainer } from "./organization-invitations-table-container/organization-invitations-table-container";
import { ContentPageHeaderDocumentationLink } from "@containers/common/content-page-header-documentation-link/content-page-header-documentation-link";
import { getActiveReferenceData } from "@utils/data-util";
import { getSessionState, getTableSettingsState } from "@redux/selectors/root-selectors";
import { getOrganizationInvitationsDataState } from "@redux/selectors/organization-invitations-selectors";

export const OrganizationInvitationsContainer = () => {
    const modalContext = useModalContext();
    const dispatch = useDispatch();

    const { session } = useSelector(getSessionState);
    const { organizationInvitations: organizationInvitationsTableState } = useSelector(getTableSettingsState);
    const {
        isLoadingAxnAdminUserInvitations,
        axnAdminUserInvitations,
    } = useSelector(getOrganizationInvitationsDataState);
    const referenceData = useReferenceDataSelector<OrganizationInvitationsReferenceData>(ORGANIZATION_INVITATIONS_REFERENCE_DATA_TYPES);

    const [shouldLoadReferenceData, setShouldLoadReferenceData] = useState(true);
    const [shouldLoadAxnAdminUserInvitations, setShouldLoadAxnAdminUserInvitations] = useState(true);

    useEffect(() => {
        if (!referenceData.isLoading.organizationInvitations && shouldLoadReferenceData) {
            setShouldLoadReferenceData(false);
            getCommonReferenceDataReduxRequest(dispatch)(
                ORGANIZATION_INVITATIONS_REFERENCE_DATA_TYPES,
                ReferenceDataSourceTypes.SERVICE_CONFIGURATION,
                CommonReferenceDataPageTypes.ORGANIZATION_INVITATIONS
            );
        }
    }, [referenceData.isLoading.organizationInvitations, shouldLoadReferenceData]);

    const isReferenceDataPresent = useMemo(() => {
        return !!(
            referenceData.axnAdminUserPropertyBundles &&
            referenceData.axnCompanies &&
            referenceData.axnRelyingParties &&
            referenceData.auth0IdentityProviders &&
            referenceData.axnIdentityProviders &&
            referenceData.documentationLinks
        );
    }, [
        referenceData.axnAdminUserPropertyBundles,
        referenceData.axnCompanies,
        referenceData.axnRelyingParties,
        referenceData.auth0IdentityProviders,
        referenceData.axnIdentityProviders,
        referenceData.documentationLinks
    ]);

    useEffect(() => {
        if (isReferenceDataPresent && !isLoadingAxnAdminUserInvitations && shouldLoadAxnAdminUserInvitations) {
            setShouldLoadAxnAdminUserInvitations(false);
            getOrganizationInvitationsReduxRequest(dispatch)(
                referenceData.axnAdminUserPropertyBundles,
                referenceData.axnCompanies,
                referenceData.axnRelyingParties
            );
        }
    }, [isReferenceDataPresent, isLoadingAxnAdminUserInvitations, shouldLoadAxnAdminUserInvitations]);
    
    const {
        axnRelyingParties: activeAxnRelyingParties
    } = useMemo(() => {
        if (isReferenceDataPresent) {
            const { axnRelyingParties } = referenceData;

            return getActiveReferenceData({
                axnRelyingParties
            });
        }

        return {
            axnRelyingParties: []
        };
    }, [isReferenceDataPresent, referenceData.axnRelyingParties]);

    const organizationAxnRelyingParties = useMemo(() => {
        return getAllowedRelyingPartiesForOrganizationId(
            activeAxnRelyingParties,
            session.user.organizationID
        );
    }, [activeAxnRelyingParties, session.user.organizationID]);

    const allowedAxnAdminPropertyBundles = useMemo(() => {
        return getAllowedAxnAdminUserPropertyBundles(
            session,
            referenceData.axnRelyingParties,
            referenceData.axnAdminUserPropertyBundles
        );
    }, [session, referenceData.axnAdminUserPropertyBundles]);

    const renderAddOrganizationUserModal = useMemo(() => (onModalClose: () => void) => {
        return (
            <AddOrganizationUserModal
                onModalClose={onModalClose}
                session={session}
                axnCompanies={referenceData.axnCompanies}
                auth0IdentityProviders={referenceData.auth0IdentityProviders}
                axnAdminUserPropertyBundles={allowedAxnAdminPropertyBundles}
                axnRelyingParties={organizationAxnRelyingParties}
                axnIdentityProviders={referenceData.axnIdentityProviders}
                shouldReloadInvitations={true}
                shouldReloadUsers={false}
                axnAdminAvailableUserOrganizationProperties={[]}
                axnAdminAvailableUserRPProperties={[]}
            />
        );
    }, [
        session,
        allowedAxnAdminPropertyBundles,
        referenceData.axnCompanies,
        referenceData.auth0IdentityProviders,
        organizationAxnRelyingParties
    ]);

    const isLoading = useMemo(() => {
        return isLoadingAxnAdminUserInvitations || referenceData.isLoading.organizationInvitations;
    }, [isLoadingAxnAdminUserInvitations, referenceData.isLoading.organizationInvitations]);

    const isLoaded = useMemo(() => {
        return !!(
            !isLoading &&
            axnAdminUserInvitations &&
            isReferenceDataPresent &&
            !shouldLoadReferenceData &&
            !shouldLoadAxnAdminUserInvitations
        );
    }, [
        isLoading,
        axnAdminUserInvitations,
        isReferenceDataPresent,
        shouldLoadReferenceData,
        shouldLoadAxnAdminUserInvitations
    ]);

    const headerButton = useMemo(() => {
        if (session.userPermissions?.canInviteUsers) {
            const onClick = () => {
                modalContext.setModalState({
                    modalType: ModalTypes.AddOrganizationUser,
                    renderFunction: renderAddOrganizationUserModal
                });
            };
            return (
                <ButtonWithIcon
                    id="add-organization-user-invitation-button"
                    title={resources.pages.organizationInvitations.headerButton}
                    text={resources.pages.organizationInvitations.headerButton}
                    onClick={onClick}
                    icon={faPlusCircle}
                    disabled={isLoading || !isLoaded}
                />
            );
        } else {
            return null;
        }
    }, [
        isLoading,
        isLoaded,
        session.userPermissions?.canInviteUsers,
        renderAddOrganizationUserModal
    ]);

    return (
        <CardContainer isPageHeight={true}>
            <ContentPageHeader
                title={resources.pages.organizationInvitations.header}
                preHeaderButtonElement={
                    <ContentPageHeaderDocumentationLink
                        documentationLinks={referenceData.documentationLinks}
                        shortName={"organization-invitation-list-page-header"}
                    />
                }
                headerButton={headerButton}
            />
            <ContentPageBody isMaxWidth={true} isPageHeight={true}>
                {isLoaded && (
                    <OrganizationInvitationsTableContainer
                        session={session}
                        isPageHeight={true}
                        axnAdminUserInvitations={axnAdminUserInvitations}
                        axnRelyingParties={organizationAxnRelyingParties}
                        tableState={organizationInvitationsTableState}
                    />
                )}
                {isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
            </ContentPageBody>
        </CardContainer>
    );
};